/**
 * Bounded concurrency for the offline import: runs at most `limit` tasks at
 * once.
 */

/**
 * Create a task queue: `schedule` waits for a free slot then starts the task
 * without awaiting it; `drain` waits for every started task. Tasks must not
 * reject (they report their own failures).
 * @param {number} limit Maximum number of tasks running at once
 * @returns {{schedule: function(function(): Promise<void>): Promise<void>,
 *   drain: function(): Promise<void>}} The queue
 */
export const createTaskQueue = limit => {
  const active = new Set();

  // Wait for a free slot, then start the task and forget it once settled
  const schedule = async task => {
    while (active.size >= limit) {
      await Promise.race(active);
    }

    const running = task().then(() => active.delete(running));
    active.add(running);
  };

  // Wait for every started task
  const drain = async () => {
    await Promise.all(active);
  };

  return { schedule, drain };
};
